export interface NavigationGuard {
  isDirty: () => boolean;
  confirmDiscard: () => Promise<boolean>;
  cancelConfirmation: () => void;
  discardAndClose: () => void;
}

/** Shared modal ordering, independent of Svelte so navigation can stay synchronous. */
export function createNavigationGuards() {
  const guards: NavigationGuard[] = [];
  let generation = 0;
  let pending = false;
  let closing = false;
  function close(guard: NavigationGuard) {
    closing = true;
    try { guard.discardAndClose(); } finally { closing = false; }
  }
  return {
    register(guard: NavigationGuard) {
      guards.push(guard);
      return () => {
        guard.cancelConfirmation();
        const index = guards.indexOf(guard);
        if (index >= 0) guards.splice(index, 1);
      };
    },
    hasDirty: () => guards.some(guard => guard.isDirty()),
    isPending: () => pending,
    cancelPending() {
      generation++;
      pending = false;
      for (const guard of [...guards]) guard.cancelConfirmation();
    },
    request(navigate: () => void): void {
      // onClose commonly calls route.push(list). During an approved discard
      // those intermediate pushes must not overwrite the original destination.
      if (closing || pending) return;
      if (!guards.some(guard => guard.isDirty())) { navigate(); return; }
      pending = true;
      const attempt = ++generation;
      void (async () => {
        const closed = new Set<NavigationGuard>();
        try {
          while (attempt === generation) {
            const active = guards.filter(guard => !closed.has(guard));
            let topDirtyIndex = active.length - 1;
            while (topDirtyIndex >= 0 && !active[topDirtyIndex].isDirty()) topDirtyIndex--;
            if (topDirtyIndex < 0) {
              for (const guard of [...active].reverse()) { closed.add(guard); close(guard); }
              if (attempt === generation) navigate();
              return;
            }
            // A clean command palette/read-only modal must not obscure the
            // dirty form's existing inline discard panel. Never discard a
            // dirty parent merely because a child is clean.
            for (const guard of active.slice(topDirtyIndex + 1).reverse()) { closed.add(guard); close(guard); }
            if (attempt !== generation) return;
            const guard = active[topDirtyIndex];
            const accepted = await guard.confirmDiscard();
            if (attempt !== generation) return;
            // A successful save may already have closed/unmounted the form
            // before its synchronous close handler's route.push is flushed.
            // There is then no draft to guard; do not strand the record URL.
            if (!guards.includes(guard)) continue;
            if (!accepted) return;
            if (guards.includes(guard)) { closed.add(guard); close(guard); }
          }
        } finally { if (attempt === generation) pending = false; }
      })();
    }
  };
}

export const navigationGuards = createNavigationGuards();
